import { useEffect } from 'react';

import {
  PageTracker,
  SnowplowTracker,
  setUserIdentity,
  trackPPCClickEvent,
} from '@gartner-digital-markets/treebeard';
import {
  configureVisitorIdentity,
  deferVisitorIdentity,
} from 'bx-forms/browser';
import { useSession } from 'next-auth/react';

import getConfig from 'config/public';
import { isClient } from 'helpers/canUse';

import { getTracker } from './tracker';

export const sp = {
  trackPPCClickEvent: ({ transactionId, productClickPosition }) => {
    if (getConfig().snowPlow.enabled) {
      const parsed = Number(productClickPosition);
      const position = Number.isFinite(parsed) ? parsed : 0;
      trackPPCClickEvent(transactionId, position);
    }
  },
};

export const mapExperimentData = (
  experimentsStr,
  experimentUserId,
  currentPageActiveExperimentIds
) => {
  if (!experimentsStr?.includes('.')) return null;

  return experimentsStr
    .split('|')
    .filter(Boolean)
    .map((exp) => {
      const [experimentId, variantId] = exp.split('.');
      return {
        experimentUserId,
        experimentId,
        variantId,
        activePageExperiment:
          currentPageActiveExperimentIds.includes(experimentId),
      };
    });
};

/** @type {import("./types").default} */
const SnowPlowAnalytics = ({
  experiments,
  countryCode,
  currentPageActiveExperimentIds,
}) => {
  const { data: session, status } = useSession();
  // Never fall back to email or display name here — `user_id` must only ever
  // hold a real user UUID.
  const g2UserId = session?.user?.externalId || undefined;
  const trackingEnabled = isClient && getConfig().snowPlow.enabled;

  // Derived from the session status, not from the id: an authenticated session
  // without an `externalId` is still logged in. Left undefined while loading so
  // the field is omitted rather than reported wrongly.
  const loginStatus =
    status === 'loading'
      ? undefined
      : status === 'authenticated'
        ? 'logged_in'
        : 'logged_out';

  // Hashing a captured email onto `user_id` is last-write-wins, so it must
  // only run while the visitor is anonymous. While the session is still
  // loading, hold submissions rather than dropping them — an authenticated
  // visitor suppresses, which discards anything held. Note that an
  // authenticated session without an `externalId` is still authenticated.
  useEffect(() => {
    if (!trackingEnabled) {
      configureVisitorIdentity(null);
      return;
    }

    if (status === 'loading') {
      deferVisitorIdentity();
      return;
    }

    configureVisitorIdentity(
      status === 'unauthenticated'
        ? (email) => setUserIdentity({ email })
        : null
    );

    return () => configureVisitorIdentity(null);
  }, [trackingEnabled, status]);

  if (!trackingEnabled) return null;

  const tracker = getTracker();
  // _document only loads Osano when it is the primary consent manager, and
  // the script tag outlives client-side navigations, unlike the page props.
  const consentProvider = document.getElementById('osano-cookie-consent')
    ? 'osano'
    : 'onetrust';
  const experimentData = mapExperimentData(
    experiments?.User_Information_GAExperimentId,
    experiments?.User_Information_GAExperimentUserId,
    currentPageActiveExperimentIds
  );

  return (
    <>
      <SnowplowTracker
        requestId={window.xRequestId || ''}
        countryCode={countryCode}
        cookie={document?.cookie}
        experimentData={experimentData}
        {...(g2UserId && { g2UserId })}
        {...(loginStatus && { loginStatus })}
        consentProvider={consentProvider}
      />
      {status !== 'loading' && tracker && (
        <PageTracker
          pathname={document?.location.pathname}
          searchParams={document?.location?.search}
          tracker={tracker}
        />
      )}
    </>
  );
};
export default SnowPlowAnalytics;
